import React, { useState } from 'react';
import { SearchIcon, CheckCircle2Icon, PrinterIcon, MessageSquareIcon, ZapIcon, UserIcon, MapPinIcon, AlertCircleIcon, GaugeIcon, PlusIcon, CreditCardIcon, BanknoteIcon, QrCodeIcon, SmartphoneIcon, WalletIcon, ChevronDownIcon } from 'lucide-react';
import { generateToken, formatZAR } from '../hooks/useMavisData';
import { useMunicipality } from '../hooks/useMunicipality';
const paymentMethods = [{
  id: 'cash',
  label: 'Cash',
  icon: BanknoteIcon,
  description: 'Pay at vendor'
}, {
  id: 'card',
  label: 'Card',
  icon: CreditCardIcon,
  description: 'Debit or credit'
}, {
  id: 'eft',
  label: 'EFT',
  icon: WalletIcon,
  description: 'Bank transfer'
}, {
  id: 'qr',
  label: 'QR Code',
  icon: QrCodeIcon,
  description: 'Scan to pay'
}, {
  id: 'mobile',
  label: 'Mobile',
  icon: SmartphoneIcon,
  description: 'Mobile money'
}];
export function Vending() {
  const {
    selected: municipality
  } = useMunicipality();
  const [step, setStep] = useState(1);
  const [meterNumber, setMeterNumber] = useState('');
  const [amount, setAmount] = useState<number | ''>('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [meterFound, setMeterFound] = useState(false);
  const [generatedToken, setGeneratedToken] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!meterNumber) return;
    setIsSearching(true);
    // Simulate API call
    setTimeout(() => {
      setIsSearching(false);
      setMeterFound(true);
      setStep(2);
    }, 800);
  };
  const handleGenerate = () => {
    if (!amount || !paymentMethod) return;
    setIsGenerating(true);
    // Simulate processing
    setTimeout(() => {
      setGeneratedToken(generateToken());
      setIsGenerating(false);
      setStep(3);
    }, 1500);
  };
  const resetFlow = () => {
    setStep(1);
    setMeterNumber('');
    setAmount('');
    setPaymentMethod('');
    setMeterFound(false);
    setGeneratedToken('');
  };
  const presetAmounts = [50, 100, 200, 500];
  const estimatedKwh = amount ? (Number(amount) / 2.5).toFixed(1) : '0.0';
  const selectedMethodLabel = paymentMethods.find(m => m.id === paymentMethod)?.label || '';
  return <div className="p-8 max-w-6xl mx-auto animate-in fade-in duration-500" data-id="element-2717">
      <div className="mb-8" data-id="element-2718">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2719">
          Prepaid Electricity Vending
        </h1>
        <p className="text-gray-500" data-id="element-2720">
          Generate and issue prepaid tokens for municipal smart meters.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2721">
        {/* Main Workflow Area */}
        <div className="lg:col-span-2 space-y-6" data-id="element-2722">
          {/* Progress Indicator */}
          <div className="flex items-center justify-between mb-8 relative" data-id="element-2723">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-200 -z-10" data-id="element-2724"></div>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-mavis-teal -z-10 transition-all duration-500" style={{
            width: step === 1 ? '0%' : step === 2 ? '50%' : '100%'
          }} data-id="element-2725">
            </div>

            {[1, 2, 3].map(s => <div key={s} className={`w-10 h-10 rounded-none flex items-center justify-center font-bold border-4 transition-colors ${step >= s ? 'bg-mavis-teal border-white text-white shadow-md' : 'bg-gray-100 border-white text-gray-400'}`} data-id="element-2726">

                {s < step ? <CheckCircle2Icon className="w-5 h-5" data-id="element-2727" /> : s}
              </div>)}
          </div>

          {/* Step 1: Meter Lookup */}
          {step === 1 && <div className="bg-white p-8 rounded-none shadow-sm border border-gray-100 animate-in slide-in-from-right-4" data-id="element-2728">
              <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2729">
                Step 1: Identify Meter
              </h2>
              <form onSubmit={handleSearch} data-id="element-2730">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2731">
                  Meter Number
                </label>
                <div className="flex gap-3" data-id="element-2732">
                  <div className="relative flex-1" data-id="element-2733">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2734">
                      <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2735" />
                    </div>
                    <input type="text" value={meterNumber} onChange={e => setMeterNumber(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal font-mono text-lg" placeholder="e.g. 04-1234-5678-90" required data-id="element-2736" />

                  </div>
                  <button type="submit" disabled={isSearching || !meterNumber} className="px-6 py-3 text-white font-medium rounded-none hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" style={{
                backgroundColor: municipality.accentColor
              }} data-id="element-2737">

                    {isSearching ? 'Searching...' : 'Lookup'}
                  </button>
                </div>
                <p className="mt-3 text-sm text-gray-500 flex items-center gap-1" data-id="element-2738">
                  <AlertCircleIcon className="w-4 h-4" data-id="element-2739" /> Ensure the 11 or 13
                  digit meter number is correct.
                </p>
              </form>
            </div>}

          {/* Step 2: Amount Selection */}
          {step === 2 && <div className="bg-white p-8 rounded-none shadow-sm border border-gray-100 animate-in slide-in-from-right-4" data-id="element-2740">
              <div className="flex justify-between items-center mb-6" data-id="element-2741">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-2742">
                  Step 2: Purchase Amount
                </h2>
                <button onClick={() => setStep(1)} className="text-sm text-mavis-blue hover:underline" data-id="element-2743">

                  Change Meter
                </button>
              </div>

              <div className="mb-6" data-id="element-2744">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-2745">
                  Quick Select
                </label>
                <div className="grid grid-cols-4 gap-3" data-id="element-2746">
                  {presetAmounts.map(preset => <button key={preset} onClick={() => setAmount(preset)} className={`py-3 rounded-none font-bold font-mono text-lg border-2 transition-all ${amount === preset ? 'border-mavis-gold bg-mavis-gold/10 text-mavis-teal' : 'border-gray-200 text-gray-600 hover:border-mavis-gold/50 hover:bg-gray-50'}`} data-id="element-2747">

                      R{preset}
                    </button>)}
                </div>
              </div>

              <div className="mb-8" data-id="element-2748">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2749">
                  Custom Amount (ZAR)
                </label>
                <div className="relative" data-id="element-2750">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-2751">
                    <span className="text-gray-500 font-bold text-lg" data-id="element-2752">R</span>
                  </div>
                  <input type="number" value={amount} onChange={e => setAmount(Number(e.target.value) || '')} className="block w-full pl-10 pr-4 py-4 border-2 border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal font-mono text-2xl" placeholder="0.00" data-id="element-2753" />

                </div>
              </div>

              {/* Method of Payment */}
              <div className="mb-8" data-id="element-2754">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2755">
                  Method of Payment
                </label>
                <div className="relative" data-id="element-2756">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-2757">
                    <CreditCardIcon className="w-5 h-5 text-gray-400" data-id="element-2758" />
                  </div>
                  <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className={`block w-full pl-12 pr-4 py-3 border-2 rounded-none appearance-none bg-white text-base font-medium cursor-pointer focus:ring-mavis-teal focus:border-mavis-teal transition-colors ${paymentMethod ? 'border-mavis-gold bg-mavis-gold/5 text-mavis-teal' : 'border-gray-300 text-gray-700'}`} data-id="element-2759">

                    <option value="" data-id="element-2760">Select payment method...</option>
                    {paymentMethods.map(method => <option key={method.id} value={method.id} data-id="element-2761">
                        {method.label} — {method.description}
                      </option>)}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-2762">
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-2763" />
                  </div>
                </div>
                {!paymentMethod && amount && <p className="mt-2 text-xs text-mavis-orange flex items-center gap-1" data-id="element-2764">
                    <AlertCircleIcon className="w-3 h-3" data-id="element-2765" /> Please select a
                    payment method to continue.
                  </p>}
              </div>

              <div className="bg-blue-50 p-4 rounded-none border border-blue-100 mb-8 flex items-center justify-between" data-id="element-2766">
                <div className="flex items-center gap-3" data-id="element-2767">
                  <ZapIcon className="w-6 h-6 text-mavis-blue" data-id="element-2768" />
                  <div data-id="element-2769">
                    <p className="text-sm text-blue-800 font-medium" data-id="element-2770">
                      Estimated Units
                    </p>
                    <p className="text-xs text-blue-600" data-id="element-2771">
                      Based on current municipal tariff (R2.50/kWh)
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-2772">
                  <span className="text-2xl font-bold text-mavis-teal font-mono" data-id="element-2773">
                    {estimatedKwh}
                  </span>
                  <span className="text-sm text-mavis-teal ml-1 font-medium" data-id="element-2774">
                    kWh
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-3" data-id="element-2775">
                <button onClick={() => setStep(1)} className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-none hover:bg-gray-50 transition-colors" data-id="element-2776">

                  Back
                </button>
                <button onClick={handleGenerate} disabled={!amount || !paymentMethod || isGenerating} className="px-8 py-3 text-white font-medium rounded-none hover:opacity-90 disabled:opacity-50 flex items-center gap-2 transition-colors" style={{
              backgroundColor: municipality.accentColor
            }} data-id="element-2777">

                  {isGenerating ? 'Processing...' : 'Generate Token'}
                  {!isGenerating && <ZapIcon className="w-4 h-4" data-id="element-2778" />}
                </button>
              </div>
            </div>}

          {/* Step 3: Token Generated */}
          {step === 3 && <div className="bg-white p-8 rounded-none shadow-sm border border-mavis-green border-t-4 animate-in zoom-in-95 duration-300" data-id="element-2779">
              <div className="text-center mb-8" data-id="element-2780">
                <div className="w-16 h-16 bg-mavis-green/20 rounded-none flex items-center justify-center mx-auto mb-4" data-id="element-2781">
                  <CheckCircle2Icon className="w-8 h-8 text-mavis-green" data-id="element-2782" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-2783">
                  Transaction Successful
                </h2>
                <p className="text-gray-500 mt-1" data-id="element-2784">
                  Token generated for meter {meterNumber}
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-none border border-gray-200 mb-8 text-center" data-id="element-2785">
                <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-3" data-id="element-2786">
                  Prepaid Electricity Token
                </p>
                <div className="font-mono text-3xl md:text-4xl font-bold text-mavis-teal tracking-widest break-all" data-id="element-2787">
                  {generatedToken}
                </div>
                <div className="mt-4 flex justify-center gap-8 text-sm text-gray-600" data-id="element-2788">
                  <span data-id="element-2789">
                    Amount: <strong data-id="element-2790">{formatZAR(Number(amount))}</strong>
                  </span>
                  <span data-id="element-2791">
                    Units: <strong data-id="element-2792">{estimatedKwh} kWh</strong>
                  </span>
                  <span data-id="element-2793">
                    Paid via: <strong data-id="element-2794">{selectedMethodLabel}</strong>
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2795">
                <button className="flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-none hover:border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-2796">
                  <PrinterIcon className="w-5 h-5" data-id="element-2797" /> Print Receipt
                </button>
                <button className="flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-none hover:border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-2798">
                  <MessageSquareIcon className="w-5 h-5" data-id="element-2799" /> SMS Token
                </button>
                <button onClick={resetFlow} className="flex items-center justify-center gap-2 px-6 py-3 bg-mavis-teal text-white font-medium rounded-none hover:bg-mavis-teal/90 transition-colors" data-id="element-2800">

                  <PlusIcon className="w-5 h-5" data-id="element-2801" /> New Transaction
                </button>
              </div>
            </div>}
        </div>

        {/* Sidebar Info Area */}
        <div className="space-y-6" data-id="element-2802">
          {/* Meter Details Card */}
          <div className={`bg-white rounded-none shadow-sm border border-gray-100 overflow-hidden transition-opacity duration-500 ${meterFound ? 'opacity-100' : 'opacity-50 pointer-events-none'}`} data-id="element-2803">

            <div className="bg-mavis-teal p-4 text-white" data-id="element-2804">
              <h3 className="font-semibold flex items-center gap-2" data-id="element-2805">
                <GaugeIcon className="w-5 h-5 text-mavis-gold" data-id="element-2806" />
                Meter Details
              </h3>
            </div>
            <div className="p-5 space-y-4" data-id="element-2807">
              {meterFound ? <>
                  <div data-id="element-2808">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2809">
                      Customer
                    </p>
                    <p className="font-medium text-gray-900 flex items-center gap-2" data-id="element-2810">
                      <UserIcon className="w-4 h-4 text-gray-400" data-id="element-2811" /> Sipho
                      Ndlovu
                    </p>
                  </div>
                  <div data-id="element-2812">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2813">
                      Address
                    </p>
                    <p className="text-sm text-gray-700 flex items-start gap-2" data-id="element-2814">
                      <MapPinIcon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" data-id="element-2815" />
                      45 Mxenge Rd, Khayelitsha, 7784
                    </p>
                  </div>
                  <div className="pt-4 border-t border-gray-100 grid grid-cols-2 gap-4" data-id="element-2816">
                    <div data-id="element-2817">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2818">
                        Tariff
                      </p>
                      <p className="font-medium text-gray-900" data-id="element-2819">Residential</p>
                    </div>
                    <div data-id="element-2820">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2821">
                        Status
                      </p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-none text-xs font-medium bg-green-100 text-green-800" data-id="element-2822">
                        Active
                      </span>
                    </div>
                  </div>
                </> : <div className="text-center py-8 text-gray-400" data-id="element-2823">
                  <GaugeIcon className="w-12 h-12 mx-auto mb-3 opacity-20" data-id="element-2824" />
                  <p className="text-sm" data-id="element-2825">
                    Enter a meter number to view details
                  </p>
                </div>}
            </div>
          </div>

          {/* Recent History for Meter */}
          {meterFound && <div className="bg-white rounded-none shadow-sm border border-gray-100 p-5 animate-in fade-in" data-id="element-2826">
              <h3 className="font-semibold text-gray-900 mb-4" data-id="element-2827">
                Recent Purchases
              </h3>
              <div className="space-y-3" data-id="element-2828">
                {[{
              date: '12 Oct 2023',
              amount: 150,
              units: '60.0'
            }, {
              date: '28 Sep 2023',
              amount: 200,
              units: '80.0'
            }, {
              date: '15 Sep 2023',
              amount: 100,
              units: '40.0'
            }].map((hist, i) => <div key={i} className="flex justify-between items-center text-sm p-2 hover:bg-gray-50 rounded-none" data-id="element-2829">

                    <div data-id="element-2830">
                      <p className="font-medium text-gray-900" data-id="element-2831">{hist.date}</p>
                      <p className="text-xs text-gray-500" data-id="element-2832">{hist.units} kWh</p>
                    </div>
                    <div className="font-mono font-medium text-mavis-teal" data-id="element-2833">
                      {formatZAR(hist.amount)}
                    </div>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}